import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Search, MapPin, ChevronRight, Mountain, PartyPopper, Utensils, Landmark, Leaf, Dumbbell, HeartPulse, Moon, ArrowRight, Heart, Star, Users, Shield, Caravan, Paintbrush, CalendarDays, Handshake } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ExperienceCard, ExperienceCardSkeleton } from '@/components/ExperienceCard';
import { experiencesApi, explorersApi } from '@/api';
import { StarRating } from '@/components/StarRating';

const CATEGORY_ICONS = {
  adventure: Mountain,
  fun: PartyPopper,
  food: Utensils,
  culture: Landmark,
  nature: Leaf,
  sport: Dumbbell,
  healing: HeartPulse,
  nightlife: Moon,
  vanlife: Caravan,
  artisanat: Paintbrush,
  local_events: CalendarDays
};

// Beautiful travel images
const HERO_IMAGE = 'https://images.unsplash.com/photo-1506929562872-bb421503ef21?w=1920&q=80';
const CATEGORY_IMAGES = {
  adventure: 'https://images.unsplash.com/photo-1551632811-561732d1e306?w=800&q=80',
  fun: 'https://images.unsplash.com/photo-1758523981365-2bdbacf0ef8b?w=800&q=80',
  food: 'https://images.unsplash.com/photo-1673254194762-3b6625775a51?w=800&q=80',
  culture: 'https://images.unsplash.com/photo-1763935841627-2caf65b9736d?w=800&q=80',
  nature: 'https://images.unsplash.com/photo-1680674917495-745fec9f161a?w=800&q=80',
  sport: 'https://images.unsplash.com/photo-1568278168565-0a3403dc9225?w=800&q=80',
  healing: 'https://images.unsplash.com/photo-1545205597-3d9d02c29597?w=800&q=80',
  nightlife: 'https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?w=800&q=80',
  vanlife: 'https://images.unsplash.com/photo-1591259495353-e42d959373bf?w=800&q=80',
  artisanat: 'https://images.unsplash.com/photo-1766499670904-edab815e8fe3?w=800&q=80',
  local_events: 'https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?w=800&q=80'
};

const CTA_IMAGE = 'https://images.unsplash.com/photo-1502791451862-7bd8c1df43a7?w=1920&q=80';

const HomePage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [featuredExperiences, setFeaturedExperiences] = useState([]);
  const [featuredEvents, setFeaturedEvents] = useState([]);
  const [explorers, setExplorers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [expData, explorerData, eventsData] = await Promise.all([
          experiencesApi.getFeatured(),
          explorersApi.getAll({ limit: 4 }),
          experiencesApi.getFeaturedEvents()
        ]);
        setFeaturedExperiences(expData);
        setExplorers(explorerData);
        setFeaturedEvents(eventsData || []);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/explore?search=${encodeURIComponent(searchQuery)}`);
    }
  };

  const categories = [
    { id: 'adventure', name: t('category.adventure'), name_en: 'Adventure & Nature' },
    { id: 'fun', name: t('category.fun'), name_en: 'Fun Activities' },
    { id: 'food', name: t('category.food'), name_en: 'Food & Culinary' },
    { id: 'culture', name: t('category.culture'), name_en: 'Culture & History' },
    { id: 'sport', name: t('category.sport'), name_en: 'Sports' },
    { id: 'healing', name: t('category.healing'), name_en: 'Healing & Retreat' },
    { id: 'vanlife', name: t('category.vanlife'), name_en: 'Vanlife' },
    { id: 'artisanat', name: t('category.artisanat'), name_en: 'Crafts' },
    { id: 'local_events', name: t('category.local_events'), name_en: 'Local Events' }
  ];

  return (
    <main className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center" data-testid="hero-section">
        <img 
          src={HERO_IMAGE}
          alt="Tropical paradise"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 py-20 w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl"
          >
            <span className="inline-block px-4 py-2 bg-primary/90 text-white rounded-full text-sm font-medium mb-6">
              {i18n.language === 'fr' ? '✨ Expériences authentiques' : '✨ Authentic experiences'}
            </span>
            
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
              {i18n.language === 'fr' ? (
                <>Vivez des <span className="text-primary">aventures</span> inoubliables</>
              ) : (
                <>Live <span className="text-primary">unforgettable</span> adventures</>
              )}
            </h1>
            
            <p className="text-xl text-white/90 mb-10 leading-relaxed">
              {t('hero.subtitle')}
            </p>
            
            {/* Search Bar */}
            <form onSubmit={handleSearch} className="bg-white rounded-2xl p-2 flex items-center gap-2 shadow-2xl max-w-xl" data-testid="search-form">
              <div className="flex items-center gap-2 px-4 text-gray-400">
                <MapPin className="w-5 h-5" />
              </div>
              <input
                type="text"
                placeholder={t('hero.search_placeholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1 py-3 px-2 text-gray-800 placeholder:text-gray-400 outline-none text-lg"
                data-testid="search-input"
              />
              <Button 
                type="submit"
                size="lg"
                className="bg-primary hover:bg-primary/90 text-white rounded-xl px-8 py-6"
                data-testid="search-btn"
              >
                <Search className="w-5 h-5 md:mr-2" />
                <span className="hidden md:inline">{t('common.search')}</span>
              </Button>
            </form>

            {/* Stats */}
            <div className="flex gap-8 mt-10">
              <div className="text-white">
                <div className="text-3xl font-bold">500+</div>
                <div className="text-white/70 text-sm">{i18n.language === 'fr' ? 'Expériences' : 'Experiences'}</div>
              </div>
              <div className="text-white">
                <div className="text-3xl font-bold">200+</div>
                <div className="text-white/70 text-sm">{i18n.language === 'fr' ? 'Explorateurs' : 'Explorers'}</div>
              </div>
              <div className="text-white">
                <div className="text-3xl font-bold">50+</div>
                <div className="text-white/70 text-sm">{i18n.language === 'fr' ? 'Pays' : 'Countries'}</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-20 bg-gradient-to-b from-orange-50 to-white" data-testid="categories-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">{t('home.categories')}</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {i18n.language === 'fr' 
                ? 'Trouvez l\'expérience parfaite parmi nos catégories soigneusement sélectionnées'
                : 'Find the perfect experience from our carefully curated categories'}
            </p>
          </div>
          
          {/* Category Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
            {categories.map((category, index) => {
              const Icon = CATEGORY_ICONS[category.id] || Mountain;
              
              return (
                <motion.div
                  key={category.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Link 
                    to={`/explore?category=${category.id}`} 
                    className="group block relative rounded-2xl overflow-hidden aspect-[4/3] shadow-lg hover:shadow-2xl transition-all duration-300"
                    data-testid={`category-${category.id}`}
                  >
                    <img 
                      src={CATEGORY_IMAGES[category.id]}
                      alt={category.name}
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-5">
                      <div className="flex items-center gap-3 text-white">
                        <div className="w-10 h-10 rounded-full bg-primary/90 flex items-center justify-center">
                          <Icon className="w-5 h-5" />
                        </div>
                        <span className="text-xl font-semibold">{category.name}</span>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-white" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">{t('home.how_it_works')}</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
            {[
              { icon: Search, title: t('home.step1_title'), desc: t('home.step1_desc'), color: 'bg-orange-100 text-primary' },
              { icon: Users, title: t('home.step2_title'), desc: t('home.step2_desc'), color: 'bg-orange-100 text-primary' },
              { icon: Heart, title: t('home.step3_title'), desc: t('home.step3_desc'), color: 'bg-orange-100 text-primary' }
            ].map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className={`w-20 h-20 rounded-2xl ${step.color} flex items-center justify-center mx-auto mb-6`}>
                  <step.icon className="w-10 h-10" />
                </div>
                <div className="text-sm font-bold text-primary mb-2">
                  {i18n.language === 'fr' ? `Étape ${index + 1}` : `Step ${index + 1}`}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
                <p className="text-gray-600">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Experiences */}
      <section className="py-20 bg-orange-50/50" data-testid="featured-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-end justify-between mb-10">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900">{t('home.featured')}</h2>
              <p className="text-gray-600 mt-2">
                {i18n.language === 'fr' ? 'Les expériences les mieux notées' : 'Top-rated experiences'}
              </p>
            </div>
            <Link 
              to="/explore"
              className="hidden md:flex items-center gap-1 text-primary font-semibold hover:gap-2 transition-all"
            >
              {i18n.language === 'fr' ? 'Voir tout' : 'View all'} <ChevronRight className="w-5 h-5" />
            </Link>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {loading ? (
              [...Array(6)].map((_, i) => <ExperienceCardSkeleton key={i} />)
            ) : featuredExperiences.length > 0 ? (
              featuredExperiences.map((exp) => (
                <ExperienceCard key={exp.experience_id} experience={exp} />
              ))
            ) : (
              <div className="col-span-full text-center py-16 bg-white rounded-2xl">
                <div className="w-20 h-20 rounded-full bg-orange-100 flex items-center justify-center mx-auto mb-6">
                  <Mountain className="w-10 h-10 text-primary" />
                </div>
                <p className="text-xl text-gray-600 mb-4">
                  {i18n.language === 'fr' ? 'Aucune expérience disponible pour le moment' : 'No experiences available yet'}
                </p>
                <Link to="/register">
                  <Button className="bg-primary hover:bg-primary/90">
                    {i18n.language === 'fr' ? 'Devenez explorateur' : 'Become an explorer'}
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Featured Events Section */}
      {featuredEvents.length > 0 && (
        <section className="py-20 bg-gradient-to-br from-purple-50 to-pink-50" data-testid="events-section">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="flex justify-between items-end mb-12">
              <div>
                <span className="text-sm font-medium text-purple-600 uppercase tracking-wider">
                  {i18n.language === 'fr' ? 'Ne manquez pas' : "Don't miss"}
                </span>
                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mt-2">
                  {i18n.language === 'fr' ? 'Événements & Retraites' : 'Events & Retreats'}
                </h2>
                <p className="text-gray-600 mt-2">
                  {i18n.language === 'fr' 
                    ? 'Festivals, marchés, retraites bien-être dans votre région' 
                    : 'Festivals, markets, wellness retreats in your area'}
                </p>
              </div>
              <Link 
                to="/explore?category=local_events" 
                className="hidden md:flex items-center gap-2 text-purple-600 font-medium hover:gap-3 transition-all"
              >
                {i18n.language === 'fr' ? 'Tous les événements' : 'All events'}
                <ChevronRight className="w-5 h-5" />
              </Link>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredEvents.map((event) => (
                <Link 
                  key={event.experience_id} 
                  to={`/experience/${event.experience_id}`}
                  className="group"
                >
                  <div className="bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                    <div className="relative h-48">
                      <img 
                        src={event.images?.[0] || 'https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?w=800'}
                        alt={event.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      {event.is_premium && (
                        <div className="absolute top-3 left-3 bg-gradient-to-r from-yellow-400 to-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                          <Star className="w-3 h-3" fill="white" />
                          À la une
                        </div>
                      )}
                      <div className="absolute top-3 right-3 bg-purple-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                        {event.category === 'healing' || event.categories?.includes('healing') 
                          ? (i18n.language === 'fr' ? 'Retraite' : 'Retreat')
                          : (i18n.language === 'fr' ? 'Événement' : 'Event')
                        }
                      </div>
                    </div>
                    <div className="p-5">
                      <h3 className="font-bold text-lg text-gray-900 mb-2 line-clamp-2 group-hover:text-purple-600 transition-colors">
                        {event.title}
                      </h3>
                      <div className="flex items-center gap-2 text-gray-500 text-sm mb-3">
                        <MapPin className="w-4 h-4" />
                        <span>{event.location}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-purple-600 font-bold">
                          {event.price > 0 ? `${event.price}€` : (i18n.language === 'fr' ? 'Gratuit' : 'Free')}
                        </span>
                        {event.organizer_name && (
                          <span className="text-xs text-gray-400">
                            par {event.organizer_name}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
            
            <div className="text-center mt-8 md:hidden">
              <Link to="/explore?category=local_events">
                <Button variant="outline" className="border-purple-300 text-purple-600 hover:bg-purple-50">
                  {i18n.language === 'fr' ? 'Voir tous les événements' : 'View all events'}
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Trust Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { icon: Shield, label: i18n.language === 'fr' ? 'Paiement sécurisé' : 'Secure payment' },
              { icon: Star, label: i18n.language === 'fr' ? 'Guides vérifiés' : 'Verified guides' },
              { icon: Heart, label: i18n.language === 'fr' ? 'Expériences uniques' : 'Unique experiences' },
              { icon: Users, label: i18n.language === 'fr' ? 'Communauté active' : 'Active community' }
            ].map((item, index) => (
              <div key={index} className="flex flex-col items-center">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-3">
                  <item.icon className="w-6 h-6 text-primary" />
                </div>
                <span className="text-sm font-medium text-gray-700">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Explorers Section */}
      {explorers.length > 0 && (
        <section className="py-20 bg-orange-50/50" data-testid="explorers-section">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900">{t('home.explorers')}</h2>
              <p className="text-gray-600 mt-2">
                {i18n.language === 'fr' ? 'Rencontrez nos guides passionnés' : 'Meet our passionate guides'}
              </p>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {explorers.map((explorer) => (
                <Link
                  key={explorer.user_id}
                  to={`/explorer/${explorer.user_id}`}
                  className="bg-white rounded-2xl p-6 text-center shadow-md hover:shadow-xl transition-all group relative"
                  data-testid={`explorer-${explorer.user_id}`}
                >
                  {/* Demo Badge - for demo profiles */}
                  {(explorer.user_id?.startsWith('demo_') || explorer.user_id?.startsWith('guide_')) && (
                    <span className="absolute top-2 right-2 bg-amber-100 text-amber-700 text-xs font-medium px-2 py-1 rounded-full">
                      {i18n.language === 'fr' ? 'Exemple' : 'Example'}
                    </span>
                  )}
                  <img 
                    src={explorer.profile_picture || explorer.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(explorer.name)}&background=f97316&color=fff&size=120`}
                    alt={explorer.name}
                    className="w-24 h-24 rounded-full object-cover mx-auto mb-4 ring-4 ring-primary/20 group-hover:ring-primary/40 transition-all"
                  />
                  <h4 className="font-bold text-lg text-gray-900">{explorer.name}</h4>
                  {explorer.location && (
                    <p className="text-sm text-gray-500 flex items-center justify-center gap-1 mt-1">
                      <MapPin className="w-3 h-3" /> {explorer.location}
                    </p>
                  )}
                  {explorer.rating > 0 && (
                    <div className="mt-3 flex justify-center">
                      <StarRating rating={explorer.rating} size="sm" />
                    </div>
                  )}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden" data-testid="cta-section">
        <img 
          src={CTA_IMAGE}
          alt="Adventure awaits"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-primary/85" />
        
        <div className="relative z-10 max-w-3xl mx-auto px-4 md:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              {i18n.language === 'fr' 
                ? 'Prêt à vivre une aventure unique ?' 
                : 'Ready to live a unique adventure?'}
            </h2>
            <p className="text-xl text-white/90 mb-10">
              {i18n.language === 'fr'
                ? 'Rejoignez XPerience et connectez-vous avec des explorateurs passionnés du monde entier.'
                : 'Join XPerience and connect with passionate explorers from around the world.'}
            </p>
            <Link to="/register">
              <Button 
                size="lg" 
                className="bg-white hover:bg-white/90 text-primary px-10 py-7 text-lg rounded-full font-bold shadow-xl hover:shadow-2xl transition-all"
                data-testid="cta-btn"
              >
                {t('hero.cta')} <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default HomePage;
